import React, { useState, useEffect, useMemo, useRef } from "react";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { I18nextProvider } from 'react-i18next';
import CssBaseline from "@mui/material/CssBaseline";
import { Box } from "@mui/material";
import createAppTheme from "./theme/theme";
import i18n from "./i18n";
import AppRoutes from "./routes/AppRoutes";
import { useDispatch, useSelector } from "react-redux";
import { setLanguage } from './store/slices/database/translationSlice';
import ToastProvider from './components/common/custom-component/ToastProvider';
import Loader from "./components/common/custom-component/Loader";
import UpdateAvailableNotice from "./components/common/UpdateAvailableNotice";
import Layout from "./layouts/Layout";
import { logger } from "helpers/logger";
import "./App.css";

function App({ domainConfig }) {
	const appId = useRef(Math.random().toString(36).substring(7));
	logger.info(`📍 appId ${appId.current} rendered`);

	const dispatch = useDispatch();
	const { loading: translationsLoading, currentLanguage } = useSelector(state => state.translations);
	const { mode: themeMode } = useSelector(state => state.theme); // ✅ Get theme mode from Redux
	const [translationsInitialized, setTranslationsInitialized] = useState(false);

	// ✅ Create theme based on language direction
	const theme = useMemo(() => {
		const isRTL = ['ar', 'he', 'fa', 'ur'].includes(currentLanguage);
		return createAppTheme(isRTL ? 'rtl' : 'ltr', themeMode);
	}, [currentLanguage, themeMode]);

	// ✅ Set document direction and language
	useEffect(() => {
		const isRTL = ['ar', 'he', 'fa', 'ur'].includes(currentLanguage);
		document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
		document.documentElement.lang = currentLanguage;
	}, [currentLanguage]);

	// Load initial translations
	useEffect(() => {
		const initializeTranslations = async () => {
			try {
				const savedLanguage = localStorage.getItem('language') || 'en';
				dispatch(setLanguage(savedLanguage));
				setTranslationsInitialized(true);
			} catch (error) {
				console.error('❌ Failed to initialize translations:', error);
				setTranslationsInitialized(true);
			}
		};

		initializeTranslations();
	}, [dispatch]);

	// Show loading while translations are being fetched
	if (!translationsInitialized || translationsLoading && (window.self === window.top)) {
		return (
			<Box sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100vh',
				flexDirection: 'column',
				gap: 2
			}}>
				<Loader variant="circular" message="Loading translations..." size="small" />
			</Box>
		);
	}

	return (
		<I18nextProvider i18n={i18n}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<UpdateAvailableNotice />
				<ToastProvider>
					<BrowserRouter>
						<AppRoutes loginKeycloakIframeFlag={domainConfig?.login_keycloak_iframe ?? true} />
					</BrowserRouter>
				</ToastProvider>
			</ThemeProvider>
		</I18nextProvider>
	);
}

export default App;